<template>
  <div class="order-form">
    <Spin v-if="loading" fix></Spin>
    <Form v-else ref="orderForm" :model="formData" :disabled="loading || disabled">
      <Row :gutter="32">
        <Col span="24">
          <FormItem :label="$t('order.title')" prop="description" :rules="{ required: true, message: $t('order.description_is_required') }" required>
            <Input v-model="formData.description"></Input>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.order_reason')" prop="order_reason" :rules="{ required: true, message: $t('order.order_reason_is_required') }" required>
            <Select v-model="formData.order_reason">
              <Option value="none">{{ $t('order.no_ref') }}</Option>
              <Option value="quoted">{{ $t('order.quoted') }}</Option>
              <Option value="reorder">{{ $t('order.reorder') }}</Option>
            </Select>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32" v-if="formData.order_reason !== 'none'">
        <Col span="24">
          <FormItem :label="$t('order.quote_reference')" prop="quote_id" :required="formData.order_reason === 'quoted'">
            <QuoteSelect v-model="formData.quote_id" v-bind:quote.sync="formData.quote"></QuoteSelect>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="24">
          <FormItem :label="$t('order.short_description')" prop="short_description" :rules="{ required: true, message: this.$t('order.short_description_is_required') }" required>
            <Input v-model="formData.short_description" maxlength="30" show-word-limit></Input>
            <div class="text-align-right" style="font-size: 12px; color: rgb(128, 134, 149);">{{ $t('order.short_description_desc') }}</div>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.order_number')" prop="order_number" :rules="{ type: 'string', required: true, max: 14, message: this.$t('order.order_number_length_exceeded') }">
            <Input v-model="formData.order_number" maxlength="14" show-word-limit :disabled="preventMalicousChange"></Input>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('order.domestic_export_type')" prop="data_type" required>
            <Select v-model="formData.data_type" :disabled="preventMalicousChange">
              <Option value="order_data_type__export">{{ $t('order.domestic_export_type_export') }}</Option>
              <Option value="order_data_type__domestic">{{ $t('order.domestic_export_type_domestic') }}</Option>
            </Select>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.customer_order_number')" prop="data.purchase_order_number">
            <Input v-model="formData.data.purchase_order_number" maxlength="15" show-word-limit></Input>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('order.customer')" prop="account_id" :rules="rulesValidate.account_id">
            <CustomerInfo v-model="formData.account_id" v-bind:account.sync="formData.account" :disabled="preventMalicousChange"></CustomerInfo>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.order_date')" prop="date" :rules="{ required: true, message: $t('order.date_is_required') }" required>
            <DatePicker v-model="formData.date" type="date" format="yyyy-MM-dd"></DatePicker>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('order.order_status')" required>
            <Select v-model="formData.order_status">
              <Option :value="0">Open</Option>
              <Option :value="1">Closed</Option>
              <Option :value="2">Invalid</Option>
            </Select>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('order.process_order_category')" prop="data.process_order_category" :rules="{ required: true, message: this.$t('order.process_order_category_is_required') }" required>
            <ProcessOrderCategorySelect v-model="formData.data.process_order_category"></ProcessOrderCategorySelect>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32" v-if="formData.data_type === 'order_data_type__export'">
        <Col span="12">
          <FormItem :label="$t('order.term')" prop="data.term" required>
            <Select v-model="formData.data.term">
              <Option v-for="t in tradeTerms" :key="t.value" :value="t.value">{{ t.label }}</Option>
            </Select>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('order.sale_style')" prop="data.sale_style" required>
            <Select v-model="formData.data.sale_style">
              <Option :value="1">{{ $t('order.sale_style_from_warehouse') }}</Option>
              <Option :value="2">{{ $t('order.sale_style_from_3rdparty') }}</Option>
            </Select>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="6">
          <FormItem :label="$t('order.nsf_phase')" prop="data.nsf_phase" :rules="{ required: true, message: this.$t('order.nsf_phase_is_required') }" required>
            <NsfPhaseSelect v-model="formData.data.nsf_phase"></NsfPhaseSelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('order.product_stage')" prop="data.product_phase" :rules="{ required: true, message: this.$t('order.product_phase_is_required') }" required>
            <ProductPhaseSelect v-model="formData.data.product_phase"></ProductPhaseSelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('order.product_type')" prop="data.product_type" :rules="{ required: true, message: this.$t('order.product_type_is_required') }" required>
            <ProductTypeSelect v-model="formData.data.product_type"></ProductTypeSelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('order.order_type')" prop="data.order_type" :rules="{ required: true, message: this.$t('order.order_type_is_required') }" required>
            <OrderTypeSelect v-model="formData.data.order_type" :value="formData.data.order_type"></OrderTypeSelect>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.shipping_address')" prop="address">
            <Input v-model="formData.address" :placeholder="$t('order.address_field_not_yet_available')" disabled></Input>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('order.accounting_belongs')" prop="accounting_company" :rules="{ required: true, message: $t('order.accounting_company_is_required') }" required>
            <AccountCompanySelect v-model="formData.accounting_company"></AccountCompanySelect>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="6">
          <FormItem :label="$t('currency')" prop="currency" required>
            <CurrencySelect v-model="formData.currency"></CurrencySelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('exchange_rate')" prop="exchange_rate" :rules="{ required: true, message: $t('order.exchange_rate_is_required') }" required>
            <InputNumber v-model="formData.exchange_rate"></InputNumber>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('order.sales_person')" prop="owner_id" :rules="{ required: true, message: $t('order.owner_is_required') }" required>
            <PeopleSelect v-model="formData.owner_id" v-bind:people.sync="formData.owner"></PeopleSelect>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32" class="align-items-center">
        <Col span="12">
          <FormItem :label="$t('quote.info.payment_term')" prop="payment_term">
            <Input v-model="formData.payment_term"></Input>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('quote.info.payment_term_note')" prop="payment_term_note">
            <Input v-model="formData.payment_term_note"></Input>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.created_by_person')" prop="created_by" :rules="rulesValidate.created_by">
            <PeopleSelect v-model="formData.creator.people_id" v-bind:people.sync="formData.creator.people"></PeopleSelect>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="24">
          <FormItem :label="$t('order.remark')" prop="data.remark" required>
            <Input type="textarea" v-model="formData.data.remark" :rows="8"></Input>
          </FormItem>
        </Col>
      </Row>
      <div class="text-align-right">
        <Button type="primary" @click="handleSave" :loading="saving">{{ $t('save') }}</Button>
      </div>
    </Form>
  </div>
</template>

<script>
import { ProcessOrderCategorySelect, ProductPhaseSelect, ProductTypeSelect, OrderTypeSelect, AccountCompanySelect, UserSearchAutoComplete, NsfPhaseSelect } from '@/components/nexpcb'
import { PeopleSelect } from '@/view/people/components'
import { CustomerInfo } from '@/view/account/components'
import { QuoteSelect } from '@/view/quote/components'
import CurrencySelect from '@/components/currency'
import { mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'OrderForm',
  components: {
    ProductPhaseSelect,
    ProductTypeSelect,
    OrderTypeSelect,
    AccountCompanySelect,
    CurrencySelect,
    CustomerInfo,
    UserSearchAutoComplete,
    PeopleSelect,
    QuoteSelect,
    NsfPhaseSelect,
    ProcessOrderCategorySelect
  },
  props: {
    orderData: {
      type: Object
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    currentUser: {
      type: Object,
      required: false
    },
    afterSaved: Function
  },
  computed: {
    preventMalicousChange () {
      return !!this.formData.erp_order
    }
  },
  data () {
    return {
      saving: false,
      allowSyncToErp: false,
      formData: {
        currency: 'USD',
        order_status: 'open',
        data_type: 'order_data_type__export',
        accounting_company: '',
        data: {
          order_type: 'pcba',
          product_phase: 'prototype',
          nsf_phase: 'S1',
          process_order_category: null,
          product_type: 'A',
          term: 'FOB',
          sale_style: 1,
          purchase_order_number: null,
          remark: `项目名称
交期
付款方式`
        },
        creator: this.currentUser || {}
      },
      tradeTerms: [
        { value: 'FOB', label: 'FOB' },
        { value: 'CFR', label: 'CFR' },
        { value: 'C&I', label: 'C&I' },
        { value: 'CIF', label: 'CIF' },
        { value: 'CPT', label: 'CPT' },
        { value: 'CIP', label: 'CIP' },
        { value: 'DAF', label: 'DAF' },
        { value: 'DES', label: 'DES' },
        { value: 'DEQ', label: 'DEQ' },
        { value: 'DDU', label: 'DDU' },
        { value: 'DDP', label: 'DDP' },
        { value: 'FCA', label: 'FCA' },
        { value: 'FAS', label: 'FAS' },
        { value: 'EXW', label: 'EXW' },
        { value: 'DAP', label: 'DAP' }
      ],
      rulesValidate: {
        account_id: [
          { type: 'number', required: true, message: this.$t('order.select_customer_message') },
          {
            validator: (rule, value, callback) => {
              if (!this.formData.account || !this.formData.account.account_number) {
                return callback(new Error(this.$t('order.customer_should_have_account_number')))
              } else {
                callback()
              }
            }
          }
        ],
        created_by: [
          {
            validator: (rule, value, callback) => {
              if (!this.formData.creator.people || !this.formData.creator.people.erp_id) {
                return callback(new Error(this.$t('order.creator_missing_required_erp_id')))
              } else {
                callback()
              }
            }
          }
        ]
      }
    }
  },
  methods: {
    ...mapActions(['createOrder', 'updateOrder', 'getAccount']),
    setFormData (orderData) {
      // Convert orderData.data to object if is null
      if (!orderData.data) {
        orderData.data = {}
      }
      if (!orderData.data.remark) {
        orderData.data.remark = `项目名称
交期
付款方式`
      }
      this.formData = orderData
    },
    async handleValidateForm() {
      await this.handleLoadAccount()
      await this.$refs['orderForm'].validate((valid) => {
        if (valid) {
          this.allowSyncToErp = true
        }
        this.$emit('update:validated', valid)
      })
    },
    async handleLoadAccount() {
      if (this.formData.account_id) {
        await this.getAccount(this.formData.account_id).then((res) => {
          this.formData.account = res
        })
      }
    },
    async handleSave () {
      await this.handleLoadAccount()
      this.$refs['orderForm'].validate((valid) => {
        if (valid) {
          this.saving = true
          // fix date
          this.formData.date = moment(this.formData.date).format('YYYY-MM-DD')
          if (!this.formData.id) {
            this.createOrder(this.formData)
              .then(resp => {
                this.$Message.success(this.$t('order.order_save_success'))
                this.afterSaved(resp.data)
              })
              .finally(() => { this.saving = false })
          } else {
            this.updateOrder({ orderId: this.formData.id, attributes: this.formData })
              .then((resp) => {
                this.$Message.success(this.$t('order.order_save_success'))
                if (this.afterSaved) {
                  this.afterSaved(resp.data)
                }
              })
              .finally(() => { this.saving = false })
          }
        }
        this.$emit('update:validated', valid)
      })
    }
  },
  watch: {
    loading () {
      if (!this.loading && this.orderData) {
        this.setFormData(this.orderData)
      }
    }
  }
}
</script>
